import { Button } from "@/shadcnComponents/ui/button";
import mixpanel from "mixpanel-browser";

import {
  NewCampaignInfluencerProfile,
  PostInfo,
} from "../CampaignInterface/NewCampaignInterface";
import {
  Calendar,
  Target,
  Briefcase,
  Users,
  Layers,
  Link,
  ImageIcon,
  VideoIcon,
} from "lucide-react";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcnComponents/ui/accordion";
import {
  Card,
  CardContent,
  CardTitle,
  CardHeader,
} from "@/shadcnComponents/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shadcnComponents/ui/avatar";
import { InfluencerDetails } from "../CampaignInterface/LiveCampaignInterface";
import { OpenInNewWindow } from "@/Helpers/helper";
import { handlePreview } from "../Helpers/Helper";
import { Eye, FileText } from "lucide-react";
import { isoDateToDateString } from "@/Helpers/times-ago";
import { useNewCampaign } from "../CustomHooks/NewCampaignHooks";
import { NoActivePlanAlertButton } from "../Components/Components";

export const CollabReviewForm = () => {
  const {
    startDate,
    endDate,
    selectedPurpose,
    masterData,
    formData,
    selectedCreators,
    previewData,
    setOpenCheckOutPopup,
  } = useNewCampaign();
  return (
    <>
      <div className="space-y-11 w-full p-6 sm:p-8 md:p-10">
        <div className="space-y-4">
          {/* Guidelines Card */}
          <Card className="border-2 border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-blue-800">
                    Product Launch Campaign
                  </h2>
                  <p className="text-sm text-blue-600">
                    Date:{" "}
                    {isoDateToDateString(
                      startDate?.toString() ??
                        formData?.campaignInfo?.campaignStartDate?.toString()
                    )}{" "}
                    -{" "}
                    {isoDateToDateString(
                      endDate?.toString() ??
                        formData?.campaignInfo?.campaignEndDate?.toString()
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Accordion Cards */}
          <Accordion
            type="multiple"
            defaultValue={["campaign-info"]}
            className="space-y-4"
          >
            {/* Campaign Info Card */}
            <AccordionItem value="campaign-info">
              <Card>
                <AccordionTrigger className="bg-gradient-to-r py-0 from-purple-100 to-pink-100">
                  <CardHeader className="p-4 hover:no-underline w-full">
                    <div className="flex items-center justify-between w-full">
                      <CardTitle className="text-lg text-purple-800 flex items-center gap-2">
                        <Briefcase className="h-5 w-5" />
                        Campaign Info
                      </CardTitle>
                    </div>
                  </CardHeader>
                </AccordionTrigger>
                <AccordionContent>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Goal
                        </span>
                        <span className="text-sm text-gray-800 bg-purple-100 px-2 py-1 rounded-full">
                          {selectedPurpose
                            ?.map(
                              (item) =>
                                masterData?.CampaignPurpose?.find(
                                  (goal) => goal?._id === item
                                )?.CollabGoalsName
                            )
                            ?.join(",")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Metric
                        </span>
                        <span className="text-sm text-gray-800 bg-blue-100 px-2 py-1 rounded-full">
                          {formData?.collabInfo?.collabGoals?.replace(
                            "member reach",
                            "Member Reach"
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Platform
                        </span>
                        <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                          {formData.collabInfo.collabPlatform?.replace(
                            "linkedin",
                            "LinkedIn"
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Industry Area
                        </span>
                        <span className="text-sm text-gray-800 bg-yellow-100 px-2 py-1 rounded-full">
                          {formData?.collabInfo?.collabIndustry}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Business Type
                        </span>
                        <span className="text-sm text-gray-800 bg-red-100 px-2 py-1 rounded-full">
                          {formData?.collabInfo?.collabBuisnessType?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </AccordionContent>
              </Card>
            </AccordionItem>

            {/* Audience Criteria Card */}
            <AccordionItem value="audience-criteria">
              <Card>
                <AccordionTrigger className="bg-gradient-to-r py-0 from-green-100 to-teal-100">
                  <CardHeader className=" p-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full">
                      <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Audience Criteria
                      </CardTitle>
                    </div>
                  </CardHeader>
                </AccordionTrigger>
                <AccordionContent>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Job Roles
                        </span>
                        <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                          {formData?.creatorInfo?.audienceCategory?.length > 0
                            ? formData?.creatorInfo?.audienceCategory
                                ?.map(
                                  (item) =>
                                    masterData?.AudienceType?.find(
                                      (category) => category?._id === item
                                    )?.LkdAudienceTypeName
                                )
                                ?.join(", ")
                            : "Not selected"}
                        </span>
                      </div>
                      {/* <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Seniority
                        </span>
                        <span className="text-sm text-gray-800 bg-blue-100 px-2 py-1 rounded-full">
                          Entry level
                        </span>
                      </div> */}
                      {/* <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Country
                        </span>
                        <span className="text-sm text-gray-800 bg-orange-100 px-2 py-1 rounded-full">
                          India
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          State
                        </span>
                        <span className="text-sm text-gray-800 bg-yellow-100 px-2 py-1 rounded-full">
                          Maharashtra
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          City
                        </span>
                        <span className="text-sm text-gray-800 bg-red-100 px-2 py-1 rounded-full">
                          Mumbai
                        </span>
                      </div> */}
                    </div>
                  </CardContent>
                </AccordionContent>
              </Card>
            </AccordionItem>

            {/* Influencer Criteria Card */}
            <AccordionItem value="influencer-criteria">
              <Card>
                <AccordionTrigger className="bg-gradient-to-r py-0 from-blue-100 to-indigo-100">
                  <CardHeader className=" p-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full">
                      <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Influencer Criteria
                      </CardTitle>
                    </div>
                  </CardHeader>
                </AccordionTrigger>
                <AccordionContent>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Followers
                        </span>
                        <span className="text-sm text-gray-800 bg-blue-100 px-2 py-1 rounded-full">
                          {`${formData?.creatorInfo?.minCreatorFollowers} - ${formData?.creatorInfo?.maxCreatorFollowers}`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Gender
                        </span>
                        <span className="text-sm text-gray-800 bg-pink-100 px-2 py-1 rounded-full">
                          {`${
                            formData?.creatorInfo?.gender?.length
                              ? `${formData?.creatorInfo?.gender}`
                              : "Not Selected"
                          }`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Past Collaboration
                        </span>
                        <span className="text-sm text-gray-800 bg-yellow-100 px-2 py-1 rounded-full">
                          {`${
                            formData?.creatorInfo?.isPastCollabs?.length
                              ? `${formData?.creatorInfo?.isPastCollabs}`
                              : "Not Selected"
                          }`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Post Format
                        </span>
                        <span className="text-sm text-gray-800 bg-purple-100 px-2 py-1 rounded-full">
                          {`${
                            formData?.postInfo?.postDrafts?.filter(
                              (item) => item?.status === 1
                            )?.[0]?.postFormat?.length
                              ? `${formData?.postInfo?.postDrafts
                                  ?.filter((item) => item?.status === 1)?.[0]
                                  ?.postFormat?.replace("/", "+")
                                  ?.replace("text", "Text")}`
                              : "Not Selected"
                          }`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          Content Category
                        </span>
                        <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                          {formData?.creatorInfo?.contentCategory?.length > 0
                            ? formData?.creatorInfo?.contentCategory
                                ?.map(
                                  (item) =>
                                    masterData?.ContentCategory?.find(
                                      (category) => category?._id === item
                                    )?.categoryName
                                )
                                ?.join(", ")
                            : "Not selected"}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </AccordionContent>
              </Card>
            </AccordionItem>

            {/* Product Details Card */}
            <AccordionItem value="product-details">
              <Card>
                <AccordionTrigger className="bg-gradient-to-r py-0 from-yellow-100 to-orange-100">
                  <CardHeader className=" p-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full">
                      <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                        <Layers className="h-5 w-5" />
                        Product Details
                      </CardTitle>
                    </div>
                  </CardHeader>
                </AccordionTrigger>
                <AccordionContent>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Product Name
                        </span>
                        <p className="text-sm text-gray-800 mt-1">
                          {formData?.productInfo?.productName}
                        </p>
                      </div>

                      {formData?.productInfo?.productLink && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Reference Link
                          </span>
                          <div
                            onClick={() =>
                              OpenInNewWindow({
                                link: formData?.productInfo?.productLink,
                              })
                            }
                            className="text-sm text-blue-600 underline flex items-center gap-1 cursor-pointer"
                          >
                            <Link className="h-4 w-4" />
                            {formData?.productInfo?.productLink}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          About the Product/Service
                        </span>
                        <p className="text-sm text-gray-800 mt-1">
                          <div
                            className="ql-editor !p-0"
                            dangerouslySetInnerHTML={{
                              __html:
                                formData?.productInfo?.productDescription ||
                                "N/A",
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </AccordionContent>
              </Card>
            </AccordionItem>
          </Accordion>

          <CampaignRequirement
            postInfo={formData?.postInfo}
            shortlistedInfluencers={previewData?.SelectedInfluencersWithCPI}
          />
          <div className="mt-6 flex justify-end">
            <NoActivePlanAlertButton
              Button={
                <Button
                  className="flex items-center gap-2"
                  style={{ backgroundColor: "#DC2627" }}
                  disabled={selectedCreators.length === 0}
                >
                  Activate Campaign
                  <ArrowRight className="h-4 w-4" />
                </Button>
              }
              onClick={() => {
                // handlePaymentClick()
                mixpanel.track(
                  "Brand Draft Campaign - Review & Go Section: Activate Campaign - clicked"
                );
                setOpenCheckOutPopup(true);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export function CampaignRequirement({
  postInfo,
  shortlistedInfluencers,
}: {
  postInfo: PostInfo;
  shortlistedInfluencers: NewCampaignInfluencerProfile[] | InfluencerDetails[];
}) {
  return (
    <Accordion
      type="multiple"
      defaultValue={["campaign-info"]}
      className="space-y-4 w-full "
    >
      <AccordionItem value="campaign-requirement">
        <Card>
          <AccordionTrigger className="bg-gradient-to-r py-0 from-orange-100 to-red-100">
            <CardHeader className=" p-4 hover:no-underline">
              <div className="flex items-center justify-between w-full">
                <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Campaign Requirement
                </CardTitle>
              </div>
            </CardHeader>
          </AccordionTrigger>
          <AccordionContent>
            <CardContent className="p-4">
              <Tabs defaultValue="draft1" className="mb-6">
                <TabsList className="w-full justify-start h-12 p-1 bg-gray-100 text-gray-500 rounded-lg">
                  {postInfo?.postDrafts
                    ?.filter((i) => i?.status)
                    ?.map((_, i) => {
                      return (
                        <TabsTrigger
                          key={i}
                          value={`draft${i + 1}`}
                          className="flex-1 data-[state=active]:bg-white data-[state=active]:text-orange-600 rounded-md transition-all"
                        >
                          Draft {i + 1}
                        </TabsTrigger>
                      );
                    })}
                </TabsList>
                {postInfo?.postDrafts
                  ?.filter((p) => p.status)
                  ?.map((item, index) => {
                    return (
                      <TabsContent
                        className="mt-6"
                        key={index}
                        value={`draft${index + 1}`}
                      >
                        <Card className="p-6 border border-orange-200">
                          <div className="space-y-6">
                            {
                              <section className="space-y-2">
                                <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                  <Users className="h-5 w-5" />
                                  Influencer
                                </h3>
                                <div className="flex gap-4">
                                  {shortlistedInfluencers
                                    ?.filter((influencer) =>
                                      item?.assignedInfluencers?.includes(
                                        influencer?._id
                                      )
                                    )
                                    .map((influencer) => (
                                      <div
                                        key={influencer.name}
                                        className="flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-full border border-orange-200"
                                      >
                                        <Avatar className="h-6 w-6">
                                          <AvatarImage
                                            src={influencer.profile}
                                            alt={influencer.name}
                                          />
                                          <AvatarFallback>
                                            {influencer.name
                                              .split(" ")
                                              .map((n) => n[0])
                                              .join("")}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm text-orange-800 font-medium">
                                          {influencer.name}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              </section>
                            }

                            <section className="space-y-2">
                              <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                <ImageIcon className="h-5 w-5" />
                                Post Format
                              </h3>
                              <div className=" w-fit px-3 py-2 rounded-lg">
                                <span className="font-medium text-sm text-gray-700">
                                  {item?.postFormat}
                                </span>
                              </div>
                            </section>

                            <section className="space-y-2">
                              <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Campaign Brief
                              </h3>
                              <p className="text-gray-700 text-sm leading-relaxed">
                                <div
                                  className="ql-editor !p-0"
                                  dangerouslySetInnerHTML={{
                                    __html: item?.postDescription,
                                  }}
                                />
                              </p>
                            </section>

                            {item?.postFormat?.includes("image") &&
                              item?.postImage.length > 0 && (
                                <section className="space-y-2">
                                  <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                    <ImageIcon className="h-5 w-5" />
                                    Images of Product/Service
                                  </h3>

                                  <div className="flex gap-4">
                                    {item?.postImage?.map((image, index) => (
                                      <div
                                        key={index}
                                        className="relative group"
                                      >
                                        <img
                                          src={image || "/placeholder.svg"}
                                          alt={`Product image ${index + 1}`}
                                          className="w-32 h-32 object-cover rounded-lg"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              )}
                            {item?.postFormat?.includes("carousel") &&
                              item?.postPdf.length > 0 && (
                                <section className="space-y-2">
                                  <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    PDF of Product/Service
                                  </h3>

                                  <div className="flex gap-4">
                                    {item?.postPdf?.map((itemPdf, pdfIndex) => (
                                      <div
                                        key={pdfIndex}
                                        className="relative group"
                                      >
                                        <div className="w-36 h-44 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center transition-all duration-200 hover:shadow-md hover:border-gray-300">
                                          <div className="mb-4">
                                            <FileText
                                              size={48}
                                              className="text-red-500"
                                            />
                                          </div>
                                          <div className="text-orange-800 text-sm font-medium">
                                            PDF {pdfIndex + 1}
                                          </div>
                                        </div>
                                        {/* Preview Button */}
                                        <div className="mt-2 flex justify-center">
                                          <Button
                                            onClick={(e) => {
                                              e.preventDefault();
                                              handlePreview(itemPdf);
                                            }}
                                            variant="outline"
                                            className=" text-gray-700 bg-orange-50 border-orange-200 hover:bg-orange-100 flex items-center gap-2 text-sm px-3 py-1 h-8"
                                          >
                                            <Eye size={16} />
                                            Preview
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              )}
                            {item?.postFormat?.includes("video") &&
                              item?.postVideo?.length > 0 && (
                                <section className="space-y-2">
                                  <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                    <VideoIcon className="h-5 w-5" />
                                    Product Videos you can use
                                  </h3>

                                  <div
                                    onClick={() =>
                                      OpenInNewWindow({
                                        link: item?.postVideo,
                                      })
                                    }
                                    className="text-sm font-normal text-orange-800 cursor-pointer "
                                  >
                                    {item?.postVideo}
                                  </div>
                                </section>
                              )}

                            <section className="space-y-2">
                              <h3 className="text-md font-semibold text-orange-800 flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Campaign Guidelines
                              </h3>

                              <p className="text-gray-700 text-sm leading-relaxed">
                                <div
                                  className="ql-editor !p-0"
                                  dangerouslySetInnerHTML={{
                                    __html: item?.postGuidelines,
                                  }}
                                />
                              </p>
                            </section>
                          </div>
                        </Card>
                      </TabsContent>
                    );
                  })}
              </Tabs>
            </CardContent>
          </AccordionContent>
        </Card>
      </AccordionItem>
    </Accordion>
  );
}
